/**
 * Google Analytics 4 (GA4) integration.
 *
 * SETUP: paste your GA4 Measurement ID below (GA Admin → Data Streams →
 * your web stream → "Measurement ID", format "G-XXXXXXXXXX"). Until a real
 * ID is set, every function here is a no-op, so the site runs unaffected.
 *
 * You can also override at build time with REACT_APP_GA_MEASUREMENT_ID.
 */
import { getCookie, setCookie } from './cookie_utils';

export const GA_MEASUREMENT_ID =
  process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-43H6SJ6EEC';

/** Name of the cookie that stores the visitor's analytics-consent choice. */
export const CONSENT_COOKIE = 'cookie_consent';

function isEnabled() {
  return GA_MEASUREMENT_ID && !GA_MEASUREMENT_ID.includes('XXXX');
}

/** "granted" | "denied" | "" (undecided). */
export function getConsent() {
  if (typeof document === 'undefined') return '';
  return getCookie(CONSENT_COOKIE);
}

export function hasConsent() {
  return getConsent() === 'granted';
}

/** Persist the visitor's choice for a year; init GA immediately on grant. */
export function setConsent(granted) {
  setCookie(CONSENT_COOKIE, granted ? 'granted' : 'denied', 365);
  if (granted) initGA();
}

/** Load gtag.js once and configure the GA4 stream. No-op without consent. */
export function initGA() {
  if (!isEnabled() || !hasConsent() || typeof window === 'undefined' || window.gtag) return;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag('js', new Date());
  // SPA: suppress the automatic page_view; we send them on route change.
  gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });
}

/** Record a virtual page view (called on every React Router navigation). */
export function trackPageView(path) {
  if (!isEnabled() || typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

/** Record a custom interaction, e.g. trackEvent('select_product', {...}). */
export function trackEvent(action, params = {}) {
  if (!isEnabled() || typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', action, params);
}
